/**
 * Curated city catalogue used by InteractiveMap.
 * Each entry carries:
 *   - id (matches contract / company city ids)
 *   - lat/lng (real GPS, used by both d3-geo projection and distance maths)
 *   - phase (gating: 1=local, 2=regional, 3=national, 4=EU)
 *   - country (FR / DE / IT / ES / BE / NL)
 *
 * Keep in sync with /app/frontend/src/data/cities.js if you add new entries
 * elsewhere — for now this module is the single source of truth for the map.
 */
export const MAP_CITIES = [
  // ── Phase 1 (locale + grandes métropoles France) ───────────────────
  { id: 'paris',       name: 'Paris',        lat: 48.8566, lng: 2.3522,  phase: 1, country: 'FR' },
  { id: 'lyon',        name: 'Lyon',         lat: 45.7640, lng: 4.8357,  phase: 1, country: 'FR' },
  { id: 'marseille',   name: 'Marseille',    lat: 43.2965, lng: 5.3698,  phase: 1, country: 'FR' },
  { id: 'bordeaux',    name: 'Bordeaux',     lat: 44.8378, lng: -0.5792, phase: 1, country: 'FR' },
  { id: 'lille',       name: 'Lille',        lat: 50.6292, lng: 3.0573,  phase: 1, country: 'FR' },
  // ── Phase 2 (régional / 2e couronne France) ────────────────────────
  { id: 'nantes',      name: 'Nantes',       lat: 47.2184, lng: -1.5536, phase: 2, country: 'FR' },
  { id: 'strasbourg',  name: 'Strasbourg',   lat: 48.5734, lng: 7.7521,  phase: 2, country: 'FR' },
  { id: 'toulouse',    name: 'Toulouse',     lat: 43.6047, lng: 1.4442,  phase: 2, country: 'FR' },
  { id: 'rennes',      name: 'Rennes',       lat: 48.1173, lng: -1.6778, phase: 2, country: 'FR' },
  // ── Phase 3 (national / villes moyennes) ───────────────────────────
  { id: 'nice',        name: 'Nice',         lat: 43.7102, lng: 7.2620,  phase: 3, country: 'FR' },
  { id: 'montpellier', name: 'Montpellier',  lat: 43.6108, lng: 3.8767,  phase: 3, country: 'FR' },
  { id: 'clermont',    name: 'Clermont-Fd',  lat: 45.7772, lng: 3.0870,  phase: 3, country: 'FR' },
  { id: 'dijon',       name: 'Dijon',        lat: 47.3220, lng: 5.0415,  phase: 3, country: 'FR' },
  // ── Phase 4 (Europe + frontières) — 17/06/2026 : ajout Portugal, UK,
  //    Maroc (zone frontalière Europe-Maghreb) suite refonte dépôts user.
  //    Volontairement PAS de Pologne (prix fracassés, exclu par design).
  { id: 'barcelona',   name: 'Barcelone',    lat: 41.3874, lng: 2.1686,  phase: 4, country: 'ES' },
  { id: 'madrid',      name: 'Madrid',       lat: 40.4168, lng: -3.7038, phase: 4, country: 'ES' },
  { id: 'lisbon',      name: 'Lisbonne',     lat: 38.7223, lng: -9.1393, phase: 4, country: 'PT' },
  { id: 'porto',       name: 'Porto',        lat: 41.1579, lng: -8.6291, phase: 4, country: 'PT' },
  { id: 'frankfurt',   name: 'Francfort',    lat: 50.1109, lng: 8.6821,  phase: 4, country: 'DE' },
  { id: 'munich',      name: 'Munich',       lat: 48.1351, lng: 11.5820, phase: 4, country: 'DE' },
  { id: 'milan',       name: 'Milan',        lat: 45.4642, lng: 9.1900,  phase: 4, country: 'IT' },
  { id: 'rome',        name: 'Rome',         lat: 41.9028, lng: 12.4964, phase: 4, country: 'IT' },
  { id: 'brussels',    name: 'Bruxelles',    lat: 50.8503, lng: 4.3517,  phase: 4, country: 'BE' },
  { id: 'amsterdam',   name: 'Amsterdam',    lat: 52.3676, lng: 4.9041,  phase: 4, country: 'NL' },
  { id: 'london',      name: 'Londres',      lat: 51.5074, lng: -0.1278, phase: 4, country: 'UK' },
  { id: 'casablanca',  name: 'Casablanca',   lat: 33.5731, lng: -7.5898, phase: 4, country: 'MA' },
  // 28/07/2026 — Recrutement local conditionné aux dépôts : ajout Zurich et
  // Istanbul pour que les viviers Suisse/Turquie restent atteignables.
  { id: 'zurich',      name: 'Zurich',       lat: 47.3769, lng: 8.5417,  phase: 4, country: 'CH' },
  { id: 'istanbul',    name: 'Istanbul',     lat: 41.0082, lng: 28.9784, phase: 4, country: 'TR' },
];

export const findCityById = (id) => MAP_CITIES.find(c => c.id === id);

/**
 * Haversine distance between two GPS points (km). Used both by the HQ-bonus
 * logic (≤200 km from headquarters → -10 % fuel) and by the renderer for
 * choosing the Bézier curvature.
 */
export const haversineKm = (a, b) => {
  if (!a || !b) return 0;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371; // Earth radius km
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return Math.round(R * c);
};
